// src/pages/AboutPrrepX.jsx
import React, { useEffect, useRef } from "react";
import "./AboutPrrepX.css";
import teamImg from "./image/prrepx_about1.png";
import home2Png from "./image/about_prrepx1.png";


/* ─── DATA ──────────────────────────────────────────────────── */

const USP_FACULTY = [
  {
    emoji: "⚛️",
    text: "Our JEE courses: by 100% team of IIT faculty only.",
    tag: "JEE",
  },
  {
    emoji: "🩺",
    text: "Our Medical Course: by 100% team of IIT & top medical alumni only.",
    tag: "NEET",
  },
  {
    emoji: "📚",
    text: "Our Class 6–12th (Board + School) courses: by 100% team of IIT/NIT & top Medical alumni.",
    tag: "Class 6–12",
  },
];

const USP_FEATURES = [
  {
    icon: "�",
    title: "Doubt Portal 24×7",
    body: "Free and open to every student — ask doubts anytime, get answers from real IIT/NIT & medical alumni. First of its kind in India.",
    accent: "#8e44ad",
  },
  {
    icon: "📖",
    title: "100% Syllabus — Completely Free",
    body: "Full-length courses by IIT/NIT & top medical alumni, 100% free. No paywalls, no shortcuts — at par with any top paid coaching in India.",
    accent: "#2980b9",
  },
  {
    icon: "🧭",
    title: "Counselling Portal",
    body: "Data-driven college & branch predictions — 10× more accurate than generic advice. Real cutoffs, real data, real results.",
    accent: "#e67e22",
  },
  {
    icon: "📚",
    title: "Premium Study Materials — Free",
    body: "High-quality notes, DPP sheets, formula sheets, and mind maps crafted by IIT/NIT alumni — completely free for every student.",
    accent: "#1a7fc1",
  },
  {
    icon: "🎯",
    title: "One-Stop Solution",
    body: "Complete Syllabus Free Lecture Series, Doubt Portal 24×7, Infinite Practices, Resources, Counselling Portal, Career Guidance — all under one roof.",
    accent: "#c0392b",
  },
];

const VISION_POINTS = [
  {
    icon: "🎓",
    headline: "100% Faculties from IIT/NIT & Top Medical Alumni",
    sub: [
      "Our JEE courses: 100% team of IIT faculty only.",
      "Our Medical Course: 100% team of IIT & top medical alumni only.",
      "Our Class 6–12th courses: 100% team of IIT/NIT & top Medical alumni.",
      "Now every student has access to the same quality content as toppers — regardless of financial background.",
    ],
  },
  {
    icon: "🌐",
    headline: "One-Stop Solution for JEE / NEET / Class 6–12",
    sub: [
      "Complete Syllabus Free Lectures with Practice and Chapterwise Tests.",
      "Free Mock Tests, PYQ Practice, 24×7 Doubt Portal, Counselling Portal.",
      "Career Guidance After 12th, Personal Mentoring, 360° Student Care.",
      "Whatever a student needs — everything is here, under one roof.",
    ],
  },
  {
    icon: "💚",
    headline: "100% Free — No Barriers, No Paywalls",
    sub: [
      "Every student can prepare fully without any financial barrier.",
      "Full syllabus, premium quality notes, doubt portal, mock tests — all free.",
      "All resources are exclusively prepared by 100% IIT/NIT & top medical alumni.",
      "World-class education accessible to every student in India.",
    ],
  },
];

const PRINCIPLES = [
  {
    number: "01",
    keyword: "Mindset",
    title: "Trust your capabilities;",
    subtitle: "never doubt yourself.",
    color: "#c0392b",
    bg: "linear-gradient(135deg,#fbeaea,#fff0f0)",
    border: "#e74c3c",
  },
  {
    number: "02",
    keyword: "Growth",
    title: "Work on weaknesses;",
    subtitle: "never just compete.",
    color: "#8e44ad",
    bg: "linear-gradient(135deg,#f3e8fb,#faf0ff)",
    border: "#9b59b6",
  },
  {
    number: "03",
    keyword: "Process",
    title: "Focus on learning;",
    subtitle: "don't rush for completion.",
    color: "#1a7fc1",
    bg: "linear-gradient(135deg,#e8f4fd,#f0f8ff)",
    border: "#2980b9",
  },
  {
    number: "04",
    keyword: "Action",
    title: "Maximise today;",
    subtitle: "don't leave it for tomorrow.",
    color: "#d35400",
    bg: "linear-gradient(135deg,#fef3e8,#fff8f0)",
    border: "#e67e22",
  },
  {
    number: "05",
    keyword: "Outcome",
    title: "Work hard today;",
    subtitle: "make tomorrow flourish.",
    color: "#1e8449",
    bg: "linear-gradient(135deg,#eafaf1,#f0fff4)",
    border: "#27ae60",
  },
];

/* ─── ANIMATION HOOK ────────────────────────────────────────── */
function useFadeIn(threshold = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("apx-visible");
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

/* ─── SUB-COMPONENTS ────────────────────────────────────────── */

const Section = ({ id, children, delay = 0 }) => {
  const ref = useFadeIn(0.1);
  return (
    <section id={id} ref={ref} className="apx-section apx-fade-in" style={{ "--delay": `${delay}ms` }}>
      {children}
    </section>
  );
};

/* ─── MAIN PAGE ─────────────────────────────────────────────── */
const AboutPrrepX = () => {

  return (
    <div className="apx-page">

      {/* ══════════ HERO ══════════ */}
      <div className="apx-hero" style={{ backgroundImage: `url(${home2Png})` }}>
        <div className="apx-hero-glow apx-glow-1" />
        <div className="apx-hero-glow apx-glow-2" />
        <div className="apx-hero-glow apx-glow-3" />

        {/* Left image — desktop only, hidden via CSS */}
        <div className="apx-hero-img-left">
          <div className="apx-hero-img-frame-left">
            <img src={teamImg} alt="PrrepX Pathshala Team" className="apx-hero-main-img" />
            <div className="apx-hero-img-overlay" />
          </div>
        </div>

        {/* Mobile hero image */}
        <div className="apx-hero-mobile-img">
          <img src={teamImg} alt="PrrepX Pathshala Team" className="apx-hero-mobile-slide-img" />
          <div className="apx-hero-mobile-img-badge">🚀 One Stop Solution - JEE/NEET & Class 6–12th</div>
        </div>

        {/* Center content */}
        <div className="apx-hero-content">
          <div className="apx-hero-badge-pill">🚀 One Stop Solution - JEE/NEET & Class 6–12th</div>
          <h1 className="apx-hero-title">
            <span className="apx-hero-blue">India's most Authentic, Premium</span>
            <br />
            <span className="apx-hero-accent">& Affordable Learning</span>
          </h1>
          <p className="apx-hero-tagline">
            PrrepX Pathshala brings together{" "}<strong>100% IIT, NIT &amp; top medical alumni</strong>{" "}— under one roof, to give every Indian student the same world-class education that was once reserved only for the privileged few.
          </p>
          
          <div className="apx-hero-buttons">
            <a href="/tutorials" className="apx-btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              Start Learning Free
            </a>
            <a href="/meetourteam" className="apx-btn-outline">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              Meet Our Team
            </a>
          </div>
          <p className="apx-alumni-text">✨ Led by IIT/NIT &amp; Top Medical Alumni</p>
        </div>

        {/* Right image — hidden via CSS */}
        <div className="apx-hero-image-col">
          <div className="apx-hero-img-frame">
            <img src={teamImg} alt="PrrepX Pathshala Team" className="apx-hero-main-img" />
            <div className="apx-hero-img-overlay" />
          </div>
        </div>
      </div>

      {/* ══════════ STATS BAR ══════════ */}
      <div className="apx-statsBar">
        <div className="apx-statsGrid">
          <div className="apx-statCard">
            <div className="apx-statNumber">JEE</div>
            <div className="apx-statLabel">100% IIT Alumni only</div>
          </div>
          <div className="apx-statCard">
            <div className="apx-statNumber">NEET</div>
            <div className="apx-statLabel">100% IIT & Medical Alumni only</div>
          </div>
          <div className="apx-statCard">
            <div className="apx-statNumber">Class 6-12th</div>
            <div className="apx-statLabel">100% IIT/NIT & Medical Alumni only</div>
          </div>
          <div className="apx-statCard">
            <div className="apx-statNumber">∞</div>
            <div className="apx-statLabel">100% Free Resources</div>
          </div>
        </div>
      </div>

      <div className="apx-body">

        {/* ══════════ GRAND VISION ══════════ */}
        <Section id="grand-vision" delay={0}>
          <div className="apx-gv-wrap">
            <div className="apx-gv-eyebrow">✦ OUR GRAND VISION</div>
            <h2 className="apx-gv-title">
              What We Are Building <span className="apx-gv-accent">For Every Indian Student</span>
            </h2>
            <p className="apx-gv-lead">
              We are building something the Indian education system has never seen before — a platform where a student
              from <strong>any financial background</strong> can walk in with a dream of cracking JEE or NEET,
              and walk out with everything they need to actually do it.
            </p>

            <div className="apx-gv-pillars">
              <div className="apx-gv-pillar">
                <div className="apx-gv-pillar-icon">🎯</div>
                <h3 className="apx-gv-pillar-head">Zero Barrier. Zero Compromise.</h3>
                <p className="apx-gv-pillar-text">
                  Top-quality video lectures, infinite practice resources, and a 24×7 Doubt Portal —
                  <strong> all completely free.</strong> A student with zero financial support should
                  never have to compromise on the quality of their preparation. That gap ends here.
                </p>
              </div>

              <div className="apx-gv-pillar">
                <div className="apx-gv-pillar-icon">🏗️</div>
                <h3 className="apx-gv-pillar-head">Strong Foundations, From Class 6</h3>
                <p className="apx-gv-pillar-text">
                  Great results begin with strong roots. We focus deeply on <strong>Class 6–12th
                  foundation building</strong> — with Olympiad-level free lectures that go far
                  beyond textbooks, creating students who don't just pass exams, but truly
                  understand the subject.
                </p>
              </div>

              <div className="apx-gv-pillar">
                <div className="apx-gv-pillar-icon">🏛️</div>
                <h3 className="apx-gv-pillar-head">One Trusted Platform. Built by the Best.</h3>
                <p className="apx-gv-pillar-text">
                  Every lecture, every note, every doubt answer is created by
                  <strong> 100% IIT, NIT &amp; top medical alumni.</strong> One place.
                  Fully trusted. No shortcuts, no paywalls, no compromises.
                  That is what PrrepX Pathshala is.
                </p>
              </div>
            </div>

            <div className="apx-gv-quote">
              <span className="apx-gv-qmark">&ldquo;</span>
              <p className="apx-gv-quote-text">
                We are not just building a platform. We are building the{" "}
                <strong>equaliser</strong> — the one place where every Indian student,
                regardless of where they come from or what they can afford,
                gets their fair shot at cracking their dream exam.
              </p>
              <span className="apx-gv-qmark apx-gv-qmark--right">&rdquo;</span>
              <div className="apx-gv-quote-sig">— PrrepX Pathshala Team</div>
            </div>
          </div>
        </Section>

        {/* ══════════ USPs ══════════ */}
        <Section id="usps" delay={0}>
          <div className="apx-section-header">
            <div className="apx-section-eyebrow">THE PRREPX ADVANTAGE</div>
            <h2 className="apx-section-title">Why PrrepX is Every Serious Aspirant's First Choice</h2>
            <p className="apx-section-sub">Things that set PrrepX Pathshala completely apart from every other coaching platform in India.</p>
          </div>

          {/* Faculty excellence banner */}
          <div className="apx-faculty-banner">
            <div className="apx-faculty-banner-label">FACULTY EXCELLENCE</div>
            <div className="apx-faculty-row">
              {USP_FACULTY.map((f, i) => (
                <div key={i} className="apx-faculty-card">
                  <span className="apx-faculty-emoji">{f.emoji}</span>
                  <div>
                    <span className="apx-faculty-tag">{f.tag}</span>
                    <p className="apx-faculty-text">{f.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Feature USP cards grid */}
          <div className="apx-usp-grid">
            {USP_FEATURES.map((u, i) => (
              <div key={i} className="apx-usp-card" style={{ "--acc": u.accent, animationDelay: `${i * 60}ms` }}>
                <div className="apx-usp-accent-bar" />
                <div className="apx-usp-icon">{u.icon}</div>
                <h4 className="apx-usp-title">{u.title}</h4>
                <p className="apx-usp-body">{u.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ══════════ PRINCIPLES ══════════ */}
        <Section id="principles" delay={200}>
          <div className="apx-section-header">
            <div className="apx-section-eyebrow">HOW WE THINK & WORK</div>
            <h2 className="apx-section-title">PrrepX Five Principles of Success</h2>
            <p className="apx-section-sub">These five principles are the foundation of everything we build and everything we teach.</p>
          </div>

          <div className="apx-principles-grid">
            {PRINCIPLES.map((p, i) => (
              <div
                key={i}
                className="apx-principle-card"
                style={{ background: p.bg, "--p-color": p.color, "--p-border": p.border, animationDelay: `${i * 80}ms` }}
              >
                <div className="apx-principle-num" style={{ color: p.color }}>{p.number}</div>
                <div className="apx-principle-keyword" style={{ color: p.color, borderColor: p.border }}>{p.keyword}</div>
                <div className="apx-principle-body">
                  <span className="apx-principle-title">{p.title}</span>
                  <span className="apx-principle-sub">{p.subtitle}</span>
                </div>
                <div className="apx-principle-accent-bar" style={{ background: p.color }} />
              </div>
            ))}
          </div>
        </Section>

        {/* ══════════ CLOSING QUOTE ══════════ */}
        <div className="apx-closing">
          <div className="apx-closing-inner">
            <div className="apx-closing-quote">"</div>
            <p className="apx-closing-text">
              We are not just building a coaching platform.<br />
              We are building <span className="apx-closing-accent">India's most equitable educational ecosystem</span> —<br />
              where every child, rich or poor, gets their fair shot at their dream.
            </p>
            <div className="apx-closing-sig">— PrrepX Pathshala Team</div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutPrrepX;
